import React, { useState, useEffect } from 'react';
import { X, Package, User, Globe, FileText, Save, Search, Plus, Printer } from 'lucide-react';
import '../styles/ConsignmentEntryFormLT.css';
import apiClient from '../services/api';

const ConsignmentEntryFormI = ({ onClose, onSave, editData = null }) => {
  const CONSIGNMENT_TYPE = 'I'; // International
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [countries, setCountries] = useState([]);
  
  // Customer Selection Modal States
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customerModalType, setCustomerModalType] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  
  const [formData, setFormData] = useState({
    ConsignmentNo: '',
    ConsignmentDate: new Date().toISOString().slice(0, 16),
    ConsignmentType: CONSIGNMENT_TYPE,
    
    // Shipper
    ShipperID: '',
    ShipperCode: '',
    ShipperName: '',
    ShipperContactPerson: '',
    ShipperAddress: '',
    ShipperPhone: '',
    ShipperCountry: '',
    ShipperCity: '',
    ShipperPostalCode: '',
    
    // Consignee
    ConsigneeID: '',
    ConsigneeCode: '',
    ConsigneeName: '',
    ConsigneeContactPerson: '',
    ConsigneeAddress: '',
    ConsigneePhone: '',
    ConsigneeCountry: '',
    ConsigneeCity: '',
    ConsigneePostalCode: '',
    
    // Package Details
    Description: '',
    NoOfPkgs: '',
    Weight: '',
    Pieces: '',
    InvoiceValue: '',
    CurrencyCode: 'USD',
    
    // International Specific
    AWBNumber: '',
    FlightNumber: '',
    CustomsValue: '',
    HSCode: '',
    IncoTerms: 'FOB',
    
    // Delivery
    CourierStatusID: '',
    DeliveryDate: '',
    CollectedBy: '',
    ReceivedBy: '',
    Remarks: ''
  });

  const [newCustomerData, setNewCustomerData] = useState({
    customername: '',
    customertype: 'CR',
    phone: '',
    email: '',
    address: '',
    cityid: '',
    taxnumber: ''
  });

  // IncoTerms options
  const incoTermsOptions = ['FOB', 'CIF', 'EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP'];
  
  // Currency options
  const currencyOptions = ['USD', 'EUR', 'GBP', 'AED', 'SAR', 'INR', 'JPY', 'CNY'];

  useEffect(() => {
    loadDropdownData();
    if (!editData) {
      generateConsignmentNumber();
    } else {
      setFormData({...editData, ConsignmentType: CONSIGNMENT_TYPE});
    }
  }, [editData]);

  const loadDropdownData = async () => {
    try {
      const [empRes, custRes, countryRes] = await Promise.all([
        apiClient.get('/Employees.php'),
        apiClient.get('/Customers.php'),
        apiClient.get('/Countries.php')
      ]);

      if (empRes.data.success) setEmployees(empRes.data.data || []);
      if (custRes.data.success) setCustomers(custRes.data.data || []);
      if (countryRes.data.success) setCountries(countryRes.data.data || []);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
      setError('Failed to load dropdown data');
    }
  };

  const generateConsignmentNumber = async () => {
    try {
      const response = await apiClient.get(`/consignmentsI.php?next-number&type=${CONSIGNMENT_TYPE}`);
      
      if (response.data.success) {
        setFormData(prev => ({ 
          ...prev, 
          ConsignmentNo: response.data.data.consignmentNo 
        }));
      }
    } catch (err) {
      console.error('Error generating number:', err);
      const timestamp = Date.now();
      setFormData(prev => ({ 
        ...prev, 
        ConsignmentNo: `CNS-I-${timestamp}` 
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openCustomerModal = (type) => {
    setCustomerModalType(type);
    setShowCustomerModal(true);
    setCustomerSearchTerm('');
  };

  const selectCustomer = (customer) => {
    if (customerModalType === 'shipper') {
      setFormData(prev => ({
        ...prev,
        ShipperID: customer.customerid,
        ShipperCode: customer.customercode,
        ShipperName: customer.customername,
        ShipperPhone: customer.phone || '',
        ShipperAddress: customer.address || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        ConsigneeID: customer.customerid,
        ConsigneeCode: customer.customercode,
        ConsigneeName: customer.customername,
        ConsigneePhone: customer.phone || '',
        ConsigneeAddress: customer.address || ''
      }));
    }
    setShowCustomerModal(false);
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomerData.customername.trim()) {
      alert('Customer name is required');
      return;
    }

    try {
      const response = await apiClient.post('/Customers.php', {
        ...newCustomerData,
        companyid: companyId,
        isactive: 1
      });
      
      if (response.data.success) {
        await loadDropdownData();
        
        const newCustomer = {
          customerid: response.data.data.customerid,
          customercode: response.data.data.customercode,
          customername: newCustomerData.customername,
          phone: newCustomerData.phone,
          address: newCustomerData.address
        };
        
        selectCustomer(newCustomer);
        
        setNewCustomerData({
          customername: '',
          customertype: 'CR',
          phone: '',
          email: '',
          address: '',
          cityid: '',
          taxnumber: ''
        });
        setShowAddCustomerForm(false);
        alert(`Customer created!\nCode: ${response.data.data.customercode}`);
      } else {
        alert('Error: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error adding customer:', err);
      alert('Error adding customer: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.ConsignmentNo.trim()) {
      setError('Consignment number is required');
      return;
    }
    
    if (!formData.ShipperName.trim()) {
      setError('Shipper name is required');
      return;
    }
    
    if (!formData.ConsigneeName.trim()) {
      setError('Consignee name is required');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const dataToSave = {
        ...formData,
        ConsignmentType: CONSIGNMENT_TYPE,
        ShipperID: formData.ShipperID || null,
        ConsigneeID: formData.ConsigneeID || null,
        CollectedBy: formData.CollectedBy || null,
        ReceivedBy: formData.ReceivedBy || null,
        CourierStatusID: formData.CourierStatusID || null,
        NoOfPkgs: formData.NoOfPkgs || 0,
        Weight: formData.Weight || 0,
        Pieces: formData.Pieces || 0,
        InvoiceValue: formData.InvoiceValue || 0,
        CustomsValue: formData.CustomsValue || 0
      };
      
      await onSave(dataToSave);
    } catch (err) {
      setError(err.message || 'Failed to save consignment');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.customername?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.customercode?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.phone?.includes(customerSearchTerm)
  );

  if (!companyId) {
    return (
      <div className="consignment-form-overlay">
        <div className="consignment-form-container" style={{ maxWidth: '600px' }}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
            <button onClick={onClose} className="btn btn-primary" style={{ marginTop: '16px' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consignment-form-overlay">
      <div className="consignment-form-container" style={{ maxWidth: '1200px' }}>
        {/* ✅ WHITE TEXT HEADER - Badge on SAME LINE using flexbox */}
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            padding: '28px 32px',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '4px solid #d97706',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }}>
                <Globe size={24} color="#ffffff" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ 
                    color: '#ffffff',
                    fontSize: '1.8rem',
                    fontWeight: '700',
                    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                  }}>
                    {editData ? 'Edit' : 'New'} Consignment - International (I)
                  </div>
                  <div style={{
                    color: '#ffffff',
                    fontSize: '1rem',
                    fontWeight: '600',
                    background: 'rgba(255,255,255,0.25)',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    display: 'inline-block',
                    border: '1px solid rgba(255,255,255,0.4)',
                    fontFamily: '"Courier New", monospace'
                  }}>
                    📋 {formData.ConsignmentNo || 'CNS-I-000001'}
                  </div>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: '#ffffff',
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              }}
            >
              <X size={20} color="#ffffff" />
            </button>
          </div>
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-content">
            
            {/* Basic Information Section */}
            <div className="form-section">
              <div className="section-header">
                <FileText size={20} />
                <h3>Basic Information</h3>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Consignment No *</label>
                  <input
                    type="text"
                    name="ConsignmentNo"
                    value={formData.ConsignmentNo}
                    onChange={handleChange}
                    required
                    readOnly
                    style={{ backgroundColor: '#f3f4f6' }}
                  />
                </div>
                
                <div className="form-group">
                  <label>Consignment Date *</label>
                  <input
                    type="datetime-local"
                    name="ConsignmentDate"
                    value={formData.ConsignmentDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>AWB Number</label>
                  <input
                    type="text"
                    name="AWBNumber"
                    value={formData.AWBNumber}
                    onChange={handleChange}
                    placeholder="Air Waybill Number"
                  />
                </div>
                
                <div className="form-group">
                  <label>Flight Number</label>
                  <input
                    type="text"
                    name="FlightNumber"
                    value={formData.FlightNumber}
                    onChange={handleChange}
                    placeholder="e.g., EK123"
                  />
                </div>
              </div>
            </div>

            {/* Shipper Section */}
            <div className="form-section">
              <div className="section-header">
                <User size={20} />
                <h3>Shipper Information</h3>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => openCustomerModal('shipper')}
                  style={{ marginLeft: 'auto' }}
                >
                  <Search size={16} />
                  Select Customer
                </button>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Shipper Code</label>
                  <input
                    type="text"
                    value={formData.ShipperCode}
                    readOnly
                    style={{ backgroundColor: '#f3f4f6' }}
                  />
                </div>
                
                <div className="form-group">
                  <label>Shipper Name *</label>
                  <input
                    type="text"
                    name="ShipperName"
                    value={formData.ShipperName}
                    onChange={handleChange}
                    required
                    placeholder="Enter shipper name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="ShipperContactPerson"
                    value={formData.ShipperContactPerson}
                    onChange={handleChange}
                    placeholder="Contact person name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="ShipperPhone"
                    value={formData.ShipperPhone}
                    onChange={handleChange}
                    placeholder="+1234567890"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="ShipperAddress"
                    value={formData.ShipperAddress}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Full address"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <select
                    name="ShipperCountry"
                    value={formData.ShipperCountry}
                    onChange={handleChange}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.countryid} value={country.countryname}>
                        {country.countryname}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="ShipperCity"
                    value={formData.ShipperCity}
                    onChange={handleChange}
                    placeholder="City name"
                  />
                </div>

                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    name="ShipperPostalCode"
                    value={formData.ShipperPostalCode}
                    onChange={handleChange}
                    placeholder="ZIP/Postal code"
                  />
                </div>
              </div>
            </div>

            {/* Consignee Section */}
            <div className="form-section">
              <div className="section-header">
                <User size={20} />
                <h3>Consignee Information</h3>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => openCustomerModal('consignee')}
                  style={{ marginLeft: 'auto' }}
                >
                  <Search size={16} />
                  Select Customer
                </button>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Consignee Code</label>
                  <input
                    type="text"
                    value={formData.ConsigneeCode}
                    readOnly
                    style={{ backgroundColor: '#f3f4f6' }}
                  />
                </div>
                
                <div className="form-group">
                  <label>Consignee Name *</label>
                  <input
                    type="text"
                    name="ConsigneeName"
                    value={formData.ConsigneeName}
                    onChange={handleChange}
                    required
                    placeholder="Enter consignee name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="ConsigneeContactPerson"
                    value={formData.ConsigneeContactPerson}
                    onChange={handleChange}
                    placeholder="Contact person name"
                  />
                </div>
                
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="ConsigneePhone"
                    value={formData.ConsigneePhone}
                    onChange={handleChange}
                    placeholder="+1234567890"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="ConsigneeAddress"
                    value={formData.ConsigneeAddress}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Full address"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <select
                    name="ConsigneeCountry"
                    value={formData.ConsigneeCountry}
                    onChange={handleChange}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.countryid} value={country.countryname}>
                        {country.countryname}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="ConsigneeCity"
                    value={formData.ConsigneeCity}
                    onChange={handleChange}
                    placeholder="City name"
                  />
                </div>

                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    name="ConsigneePostalCode"
                    value={formData.ConsigneePostalCode}
                    onChange={handleChange}
                    placeholder="ZIP/Postal code"
                  />
                </div>
              </div>
            </div>

            {/* Package Details Section */}
            <div className="form-section">
              <div className="section-header">
                <Package size={20} />
                <h3>Package & Customs Details</h3>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="Description"
                    value={formData.Description}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Package description"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Packages</label>
                  <input
                    type="number"
                    name="NoOfPkgs"
                    value={formData.NoOfPkgs}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    placeholder="0"
                  />
                </div>
                
                <div className="form-group">
                  <label>Pieces</label>
                  <input
                    type="number"
                    name="Pieces"
                    value={formData.Pieces}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    placeholder="0"
                  />
                </div>

                <div className="form-group">
                  <label>Weight (kg)</label>
                  <input
                    type="number"
                    name="Weight"
                    value={formData.Weight}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Invoice Value</label>
                  <input
                    type="number"
                    name="InvoiceValue"
                    value={formData.InvoiceValue}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label>Customs Value</label>
                  <input
                    type="number"
                    name="CustomsValue"
                    value={formData.CustomsValue}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="form-group">
                  <label>Currency</label>
                  <select
                    name="CurrencyCode"
                    value={formData.CurrencyCode}
                    onChange={handleChange}
                  >
                    {currencyOptions.map(curr => (
                      <option key={curr} value={curr}>{curr}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>HS Code</label>
                  <input
                    type="text"
                    name="HSCode"
                    value={formData.HSCode}
                    onChange={handleChange}
                    placeholder="Harmonized System Code"
                  />
                </div>
                
                <div className="form-group">
                  <label>IncoTerms</label>
                  <select
                    name="IncoTerms"
                    value={formData.IncoTerms}
                    onChange={handleChange}
                  >
                    {incoTermsOptions.map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Delivery Section */}
            <div className="form-section">
              <div className="section-header">
                <Package size={20} />
                <h3>Delivery Information</h3>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Delivery Date</label>
                  <input
                    type="datetime-local"
                    name="DeliveryDate"
                    value={formData.DeliveryDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Collected By</label>
                  <select
                    name="CollectedBy"
                    value={formData.CollectedBy}
                    onChange={handleChange}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeename}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Received By</label>
                  <select
                    name="ReceivedBy"
                    value={formData.ReceivedBy}
                    onChange={handleChange}
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeename}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Remarks</label>
                  <textarea
                    name="Remarks"
                    value={formData.Remarks}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Additional notes or remarks"
                  />
                </div>
              </div>
            </div>

          </div>

          <div className="form-footer">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="btn btn-save"
              disabled={loading}
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
            >
              {loading ? (
                <>
                  <div className="btn-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save International Consignment
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Customer Selection Modal */}
      {showCustomerModal && (
        <div className="modal-overlay" onClick={() => setShowCustomerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h3>Select {customerModalType === 'shipper' ? 'Shipper' : 'Consignee'}</h3>
              <button className="close-btn" onClick={() => setShowCustomerModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: '20px' }}>
              <div className="search-box" style={{ marginBottom: '16px' }}>
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                />
              </div>

              <button
                className="btn btn-primary"
                onClick={() => setShowAddCustomerForm(!showAddCustomerForm)}
                style={{ marginBottom: '16px' }}
              >
                <Plus size={16} />
                Add New Customer
              </button>

              {showAddCustomerForm && (
                <form onSubmit={handleAddCustomer} style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px' }}>
                  <h4>New Customer</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Customer Name *</label>
                      <input
                        type="text"
                        value={newCustomerData.customername}
                        onChange={(e) => setNewCustomerData({...newCustomerData, customername: e.target.value})}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="text"
                        value={newCustomerData.phone}
                        onChange={(e) => setNewCustomerData({...newCustomerData, phone: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        value={newCustomerData.email}
                        onChange={(e) => setNewCustomerData({...newCustomerData, email: e.target.value})}
                      />
                    </div>
                    <div className="form-group">
                      <label>Address</label>
                      <input
                        type="text"
                        value={newCustomerData.address}
                        onChange={(e) => setNewCustomerData({...newCustomerData, address: e.target.value})}
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary">Create Customer</button>
                </form>
              )}

              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCustomers.map(customer => (
                      <tr key={customer.customerid}>
                        <td>{customer.customercode}</td>
                        <td>{customer.customername}</td>
                        <td>{customer.phone || '-'}</td>
                        <td>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => selectCustomer(customer)}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsignmentEntryFormI;